import React, { useEffect, useState } from 'react';
import TradeSettingsSymbolsModal from '../modals/TradeSettingsSymbolsModal';
import TradeHistoryAnalysisModal from '../modals/TradeHistoryAnalysisModal';

const HistoryTab = ({ 
  orderHistory, 
  orderHistoryLoading,
  orderHistoryRateLimited,
  orderHistoryRateLimitUntil,
  fetchOrderHistory,
  authenticatedFetch,
  user,
  marketStatus,
  onUserUpdated,
  aiTradeConfig,
  fetchAiTradeConfig,
  dateFilter,
  setDateFilter,
  customStartDate,
  customEndDate,
  setCustomStartDate,
  setCustomEndDate
}) => {
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [showTradeSettingsSymbolsModal, setShowTradeSettingsSymbolsModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const isAdmin = user?.email === 'begite@naver.com';

  const buyEnabled = aiTradeConfig?.auto_buy?.enabled !== false;
  const sellEnabled = aiTradeConfig?.auto_sell?.enabled !== false;

  const handleDateFilterChange = (filter, start, end) => {
    setDateFilter(filter);
    if (filter === 'custom') {
      setCustomStartDate(start || '');
      setCustomEndDate(end || '');
    }
    if (fetchOrderHistory) {
      const overrides = filter === 'custom' ? { dateFilter: filter, customStartDate: start, customEndDate: end } : { dateFilter: filter };
      fetchOrderHistory(showAllUsers, overrides);
    }
  };

  const getAnalysisDateRange = () => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (dateFilter === 'today') {
      return { startDate: fmt(today), endDate: fmt(today) };
    }
    if (dateFilter === 'week') {
      const start = new Date(today);
      start.setDate(start.getDate() - 7);
      return { startDate: fmt(start), endDate: fmt(today) };
    }
    if (dateFilter === 'month') {
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      return { startDate: fmt(start), endDate: fmt(today) };
    }
    if (dateFilter === 'custom' && customStartDate && customEndDate) {
      return { startDate: customStartDate, endDate: customEndDate };
    }
    // 기본: 월간(30일) - 분석은 기본적으로 최근 1개월
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    return { startDate: fmt(start), endDate: fmt(today) };
  };

  const toggleAutoBuy = async () => {
    if (!authenticatedFetch || toggleLoading) return;
    setToggleLoading(true);
    try {
      const res = await authenticatedFetch('/api/ai-trade/config', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_buy: { enabled: !buyEnabled } })
      });
      if (res.ok) {
        await fetchAiTradeConfig?.(true);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || '설정 변경에 실패했습니다.');
      }
    } catch (e) {
      alert('설정 변경 중 오류가 발생했습니다.');
    } finally {
      setToggleLoading(false);
    }
  };

  const toggleAutoSell = async () => {
    if (!authenticatedFetch || toggleLoading) return;
    setToggleLoading(true);
    try {
      const res = await authenticatedFetch('/api/ai-trade/config', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_sell: { enabled: !sellEnabled } })
      });
      if (res.ok) {
        await fetchAiTradeConfig?.(true);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || '설정 변경에 실패했습니다.');
      }
    } catch (e) {
      alert('설정 변경 중 오류가 발생했습니다.');
    } finally {
      setToggleLoading(false);
    }
  };
  
  // 관리자 체크박스 변경 시 주문 내역 다시 불러오기
  const handleAllUsersChange = (checked) => {
    setShowAllUsers(checked);
    setSelectedOrders([]); // 체크박스 변경 시 선택 초기화
    // 주문 내역 다시 불러오기
    if (fetchOrderHistory) {
      fetchOrderHistory(checked);
    }
  };
  // 주문 내역 자동 갱신 (60초마다, rate limiting이 없을 때만)
  useEffect(() => {
    if (!fetchOrderHistory) {
      console.warn('⚠️ [주문 내역] fetchOrderHistory 함수가 없음');
      return;
    }
    
    console.log('🔄 [주문 내역] HistoryTab 마운트 - 자동 갱신 시작', {
      hasFetchOrderHistory: !!fetchOrderHistory,
      rateLimited: orderHistoryRateLimited
    });
    
    // Rate limiting이 활성화되어 있으면 초기 조회도 하지 않음
    if (orderHistoryRateLimited && orderHistoryRateLimitUntil) {
      const now = Date.now();
      if (now < orderHistoryRateLimitUntil) {
        const remainingSeconds = Math.ceil((orderHistoryRateLimitUntil - now) / 1000);
        console.log(`⏸️ [주문 내역] Rate limiting 활성화 - 자동 갱신 중지 (${remainingSeconds}초 후 재개)`);
        // 초기 조회는 하지 않지만, 나중에 재시도할 수 있도록 타이머 설정
        const retryTimeout = setTimeout(() => {
          if (fetchOrderHistory) {
            fetchOrderHistory();
          }
        }, orderHistoryRateLimitUntil - now);
        return () => clearTimeout(retryTimeout);
      }
    }
    
    // 초기 조회 (한 번만 실행, 지연 없이 즉시)
    if (fetchOrderHistory) {
      fetchOrderHistory();
    }
    
    // Rate limiting이 활성화되어 있으면 자동 갱신 중지
    if (orderHistoryRateLimited && orderHistoryRateLimitUntil) {
      const now = Date.now();
      if (now < orderHistoryRateLimitUntil) {
        console.log('⏸️ [주문 내역] Rate limiting으로 인해 자동 갱신 비활성화');
        return;
      }
    }
    
    // 60초마다 자동 갱신
    const interval = setInterval(() => {
      // Rate limiting 체크 (최신 상태 읽기)
      if (orderHistoryRateLimited && orderHistoryRateLimitUntil) {
        const now = Date.now();
        if (now < orderHistoryRateLimitUntil) {
          const remainingSeconds = Math.ceil((orderHistoryRateLimitUntil - now) / 1000);
          console.log(`⏸️ [주문 내역] Rate limiting으로 인해 자동 갱신 건너뜀 (${remainingSeconds}초 후 재개)`);
          return;
        }
      }
      
      console.log('🔄 [주문 내역] 자동 갱신 실행');
      if (fetchOrderHistory) {
        fetchOrderHistory();
      }
    }, 60000); // 60초
    
    return () => {
      clearInterval(interval);
      console.log('🛑 [주문 내역] HistoryTab 언마운트 - 자동 갱신 중지');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 빈 배열로 변경하여 마운트 시 한 번만 실행
  
  // 주문 취소
  const cancelOrder = async (orderId) => {
    if (!window.confirm('대기 중인 주문을 취소하시겠습니까?')) {
      return;
    }
    
    try {
      const response = await authenticatedFetch(`/api/trading/order/${orderId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        const data = await response.json();
        alert('주문이 취소되었습니다.');
        await fetchOrderHistory();
      } else {
        const error = await response.json();
        alert(error.error || '주문 취소에 실패했습니다.');
      }
    } catch (error) {
      console.error('주문 취소 실패:', error);
      alert('주문 취소 중 오류가 발생했습니다.');
    }
  };
  
  // 주문 선택/해제
  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };
  
  // 전체 선택/해제
  const toggleAllSelection = () => {
    if (selectedOrders.length === orderHistory.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(orderHistory.map(order => order._id || order.id).filter(Boolean));
    }
  };
  
  // 일괄 삭제
  const deleteSelectedOrders = async () => {
    if (selectedOrders.length === 0) {
      alert('삭제할 주문을 선택해주세요.');
      return;
    }
    
    const confirmMessage = isAdmin && showAllUsers
      ? `⚠️ 관리자 모드: 선택한 ${selectedOrders.length}건의 주문을 삭제하시겠습니까?\n\n모든 사용자의 주문이 삭제될 수 있습니다.\n\n대기 중인 주문은 취소되고, 완료/취소/실패된 주문은 삭제됩니다.`
      : `선택한 ${selectedOrders.length}건의 주문을 삭제하시겠습니까?\n\n대기 중인 주문은 취소되고, 완료/취소/실패된 주문은 삭제됩니다.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setIsDeleting(true);
    try {
      const response = await authenticatedFetch('/api/trading/orders/batch', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          orderIds: selectedOrders,
          allUsers: isAdmin && showAllUsers
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(data.message || `주문 ${data.total}건이 처리되었습니다.`);
        setSelectedOrders([]);
        await fetchOrderHistory();
      } else {
        const error = await response.json();
        alert(error.error || '주문 일괄 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('주문 일괄 삭제 실패:', error);
      alert('주문 일괄 삭제 중 오류가 발생했습니다.');
    } finally {
      setIsDeleting(false);
    }
  };
  // Rate limiting 남은 시간 계산
  const getRateLimitRemainingTime = () => {
    if (!orderHistoryRateLimited || !orderHistoryRateLimitUntil) return null;
    const now = Date.now();
    if (now >= orderHistoryRateLimitUntil) return null;
    const remainingSeconds = Math.ceil((orderHistoryRateLimitUntil - now) / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    return minutes > 0 ? `${minutes}분 ${seconds}초` : `${seconds}초`;
  };

  const rateLimitRemaining = getRateLimitRemainingTime();
  const { startDate: analysisStart, endDate: analysisEnd } = getAnalysisDateRange();

  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div className="history-tab">
      <div className="history-header">
        <h2>
          주문 내역 
          <span style={{
            marginLeft: '10px',
            padding: '4px 8px',
            borderRadius: '6px',
            backgroundColor: marketStatus === '주문가능시간' ? '#e6ffed' : '#f0f0f0',
            color: marketStatus === '주문가능시간' ? '#2e7d32' : '#666',
            fontWeight: 700,
            fontSize: '14px'
          }}>
            {marketStatus === '주문가능시간' ? '주문가능시간' : '정규시장종료'}
          </span>
          {orderHistoryLoading ? ' (업데이트 중...)' : ''}
          {orderHistoryRateLimited && rateLimitRemaining ? (
            <span style={{ 
              color: '#ffc107', 
              fontSize: '14px', 
              marginLeft: '10px',
              fontWeight: 'normal'
            }}>
              ⚠️ 요청 제한: {rateLimitRemaining} 후 재시도
            </span>
          ) : null}
        </h2>

        {/* 배치: 1열(위) → 2열(아래) 2줄 기본 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* 1열: 매수/매도 허용, 분석기능, 거래설정, 새로고침 */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={toggleAutoBuy}
            disabled={toggleLoading || !aiTradeConfig}
            title={buyEnabled ? 'AI 자동 매수 중단' : 'AI 자동 매수 재개'}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              cursor: toggleLoading || !aiTradeConfig ? 'not-allowed' : 'pointer',
              backgroundColor: buyEnabled ? '#28a745' : '#dc3545',
              color: '#fff',
              opacity: toggleLoading ? 0.7 : 1
            }}
          >
            {buyEnabled ? '✅ 매수 허용' : '🔴 매수금지'}
          </button>
          <button
            type="button"
            onClick={toggleAutoSell}
            disabled={toggleLoading || !aiTradeConfig}
            title={sellEnabled ? 'AI 자동 매도 중단' : 'AI 자동 매도 재개'}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              cursor: toggleLoading || !aiTradeConfig ? 'not-allowed' : 'pointer',
              backgroundColor: sellEnabled ? '#28a745' : '#dc3545',
              color: '#fff',
              opacity: toggleLoading ? 0.7 : 1
            }}
          >
            {sellEnabled ? '✅ 매도 허용' : '🔴 매도금지'}
          </button>
          <button
            type="button"
            onClick={() => setShowAnalysisModal(true)}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              backgroundColor: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            📊 분석기능
          </button>
          <button
            type="button"
            onClick={() => setShowTradeSettingsSymbolsModal(true)}
            style={{
              padding: '6px 14px',
              backgroundColor: '#667eea',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600
            }}
          >
            📋 거래설정·금지종목
          </button>
          <button
            onClick={() => fetchOrderHistory?.(showAllUsers)}
            className="btn-refresh"
            disabled={orderHistoryLoading}
            style={{
              padding: '6px 14px',
              fontSize: '13px',
              opacity: orderHistoryLoading ? 0.6 : 1
            }}
          >
            {orderHistoryLoading ? '⏳ 갱신 중...' : '🔄 새로고침'}
          </button>
          {selectedOrders.length > 0 && (
            <button
              onClick={deleteSelectedOrders}
              disabled={isDeleting || orderHistoryLoading}
              style={{
                padding: '6px 14px',
                fontSize: '13px',
                backgroundColor: isDeleting ? '#ccc' : '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isDeleting ? 'not-allowed' : 'pointer',
                fontWeight: 600
              }}
            >
              {isDeleting ? '삭제 중...' : `🗑️ 선택 삭제 (${selectedOrders.length}건)`}
            </button>
          )}
          </div>

          {/* 2열: 기간 필터, 모든 사용자 주문 보기 */}
          <div className="history-date-filter" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', color: '#666', marginRight: '4px' }}>기간:</span>
          {['all', 'today', 'week', 'month', 'custom'].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => {
                if (f === 'custom') {
                  const end = new Date();
                  const start = new Date();
                  start.setDate(start.getDate() - 7);
                  handleDateFilterChange('custom', start.toISOString().slice(0, 10), end.toISOString().slice(0, 10));
                } else {
                  handleDateFilterChange(f);
                }
              }}
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: dateFilter === f ? '#667eea' : '#fff',
                color: dateFilter === f ? '#fff' : '#333'
              }}
            >
              {f === 'all' ? '전체' : f === 'today' ? '오늘' : f === 'week' ? '1주일' : f === 'month' ? '1개월' : '기간 선택'}
            </button>
          ))}
          {dateFilter === 'custom' && (
            <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                max={customEndDate || todayStr}
                style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              ~
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                min={customStartDate}
                max={todayStr}
                style={{ padding: '4px 8px', fontSize: '13px', borderRadius: '4px', border: '1px solid #ddd' }}
              />
              <button
                type="button"
                onClick={() => fetchOrderHistory && fetchOrderHistory(showAllUsers, { dateFilter: 'custom', customStartDate, customEndDate })}
                style={{ padding: '4px 10px', fontSize: '12px', backgroundColor: '#667eea', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                적용
              </button>
            </span>
          )}
          {isAdmin && (
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px', 
              cursor: 'pointer',
              padding: '5px 10px',
              backgroundColor: showAllUsers ? '#ffc107' : '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #dee2e6'
            }}>
              <input
                type="checkbox"
                checked={showAllUsers}
                onChange={(e) => handleAllUsersChange(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: showAllUsers ? '#856404' : '#6c757d' }}>
                👑 모든 사용자 주문 보기
              </span>
            </label>
          )}
          </div>
        </div>
      </div>

      {showTradeSettingsSymbolsModal && (
        <TradeSettingsSymbolsModal
          user={user}
          authenticatedFetch={authenticatedFetch}
          onClose={() => setShowTradeSettingsSymbolsModal(false)}
          onUserUpdated={onUserUpdated}
        />
      )}
      {showAnalysisModal && (
        <TradeHistoryAnalysisModal
          onClose={() => setShowAnalysisModal(false)}
          authenticatedFetch={authenticatedFetch}
          startDate={analysisStart}
          endDate={analysisEnd}
        />
      )}
      {orderHistory.length === 0 ? (
        <div className="history-empty">
          <p>주문 내역이 없습니다.</p>
          {orderHistoryLoading && <p style={{ color: '#666', fontSize: '14px' }}>주문 내역을 불러오는 중...</p>}
        </div>
      ) : (
        <div className="history-list">
          <table className="history-table">
            <thead>
              <tr>
                <th data-column="checkbox">
                  <input
                    type="checkbox"
                    checked={selectedOrders.length === orderHistory.length && orderHistory.length > 0}
                    onChange={toggleAllSelection}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th data-column="date">일시</th>
                <th data-column="symbol">종목</th>
                <th data-column="type">구분</th>
                <th data-column="quantity">수량</th>
                <th data-column="price">가격</th>
                <th data-column="total">총액</th>
                <th data-column="status">상태</th>
                <th data-column="source">유형</th>
                <th data-column="actions">작업</th>
              </tr>
            </thead>
            <tbody>
              {orderHistory.map((order, index) => {
                // 가격과 총액 계산
                let displayPrice = order.price || order.order_price || 0;
                
                // 가격 단위 정규화 (1000원 미만이면 1000을 곱어서 표시 - 한국 주식 시장 규칙)
                // 예: 164 → 164000, 165200 → 165200 (변경 없음)
                if (displayPrice > 0 && displayPrice < 1000) {
                  displayPrice = displayPrice * 1000;
                }
                
                // 수량
                const quantity = Number(order.quantity || order.qty || 0);
                
                // 매도 주문의 경우 total_amount가 잘못 저장될 수 있으므로 직접 계산
                const isSellOrder = order.order_type === 'sell' || order.decision === 'SELL';
                let displayTotal;
                
                if (isSellOrder) {
                  // 매도 주문: 항상 수량 * 가격으로 계산 (total_amount 무시)
                  displayTotal = quantity * displayPrice;
                } else {
                  // 매수 주문: total_amount 우선 사용, 없으면 계산
                  displayTotal = order.total_amount || (quantity * displayPrice) || 0;
                }
                
                // 100원 단위 절삭 (한국 주식 시장 규칙)
                displayTotal = Math.floor(displayTotal / 100) * 100;
                
                // 주문 유형 확인 (AI 자동매매, AI 추천, 일반 주문 등)
                const orderSource = order.source || order.order_source || '일반';
                const isAITrade = orderSource === 'ai' || orderSource === 'ai-trade' || order.is_ai_trade;
                const isAIRecommend = orderSource === 'ai-recommend' || 
                                     (order.metadata && (
                                       order.metadata.get?.('watchlist_source') === 'ai-recommend' ||
                                       order.metadata.watchlist_source === 'ai-recommend'
                                     )) ||
                                     order.watchlist_source === 'ai-recommend';
                const isFromWatchlist = orderSource === 'watchlist' || 
                                      (order.metadata && (
                                        order.metadata.get?.('watchlist_id') ||
                                        order.metadata.watchlist_id
                                      )) ||
                                      order.watchlist_id;
                
                // 주문 상태에 따른 스타일
                const statusClass = order.status === 'completed' ? 'completed' :
                                   order.status === 'cancelled' ? 'cancelled' :
                                   order.status === 'failed' ? 'failed' :
                                   'pending';
                
                // 시간 포맷팅
                const orderTime = order.createdAt || order.timestamp || order.time;
                const timeDisplay = orderTime 
                  ? new Date(orderTime).toLocaleString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })
                  : 'N/A';
                
                const orderId = order._id || order.id;
                const isSelected = orderId && selectedOrders.includes(orderId);
                
                return (
                  <tr key={order.id || index} className={`order-row ${statusClass}`}>
                    <td data-column="checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOrderSelection(orderId)}
                        disabled={!orderId}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td data-column="date">{timeDisplay}</td>
                    <td data-column="symbol">
                      {order.stock_name || order.name || order.symbol || order.code || order.ticker}
                      {order.stock_name || order.name ? (
                        <span style={{ fontSize: '12px', color: '#666', marginLeft: '5px' }}>({order.symbol || order.code || order.ticker})</span>
                      ) : null}
                    </td>
                    <td data-column="type" className={order.order_type === 'buy' || order.decision === 'BUY' ? 'buy' : 'sell'}>
                      {order.order_type === 'buy' || order.decision === 'BUY' ? '매수' : '매도'}
                    </td>
                    <td data-column="quantity">{order.quantity || order.qty || 0}주</td>
                    <td data-column="price">₩{parseInt(displayPrice).toLocaleString()}</td>
                    <td data-column="total">₩{parseInt(displayTotal).toLocaleString()}</td>
                    <td data-column="status" className={`status ${statusClass}`}>
                      {order.status === 'completed' || order.status === '주문완료' ? (
                        <span style={{ color: '#28a745', fontWeight: 'bold' }}>✅ 체결</span>
                      ) : order.status === 'cancelled' || order.status === '취소' ? (
                        <span style={{ color: '#dc3545' }}>❌ 취소</span>
                      ) : order.status === 'failed' || order.status === '실패' ? (
                        <span style={{ color: '#ffc107' }}>⚠️ 실패</span>
                      ) : (
                        <span style={{ color: '#6c757d' }}>⏳ 대기</span>
                      )}
                    </td>
                    <td data-column="source">
                      {isAITrade ? (
                        <span style={{ 
                          color: '#667eea', 
                          fontSize: '12px',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: '#e8f0fe',
                          marginRight: '4px'
                        }}>
                          🤖 AI 거래
                        </span>
                      ) : isAIRecommend ? (
                        <span style={{ 
                          color: '#28a745', 
                          fontSize: '12px',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: '#d4edda',
                          marginRight: '4px'
                        }}>
                          ⭐ AI 추천
                        </span>
                      ) : isFromWatchlist ? (
                        <span style={{ 
                          color: '#17a2b8', 
                          fontSize: '12px',
                          fontWeight: 'bold',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: '#d1ecf1',
                          marginRight: '4px'
                        }}>
                          📝 관심종목
                        </span>
                      ) : (
                        <span style={{ color: '#999', fontSize: '12px' }}>일반</span>
                      )}
                    </td>
                    <td data-column="actions">
                      {order.status === 'pending' && (order._id || order.id) ? (
                        <button
                          onClick={() => cancelOrder(order._id || order.id)}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          취소
                        </button>
                      ) : (
                        <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default HistoryTab;

